<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-baby-carriage me-2"></i>{{ $t('page.maternity', 'Maternity') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-blue" @click="loadAll">
            <i class="mdi mdi-refresh"></i>
          </button>
        </div>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-info">{{ labourStats.active || 0 }}</div>
          <div class="hms-stat-lbl">Active Labours</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-danger">{{ labourStats.at_action_line || 0 }}</div>
          <div class="hms-stat-lbl">At Action Line</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-success">{{ labourStats.delivered_today || 0 }}</div>
          <div class="hms-stat-lbl">Delivered Today</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-info">{{ ancStats.visits_today || 0 }}</div>
          <div class="hms-stat-lbl">ANC Visits Today</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-warning">{{ ancStats.hypertensive || 0 }}</div>
          <div class="hms-stat-lbl">Hypertensive</div>
        </div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='labour' }" @click="tab='labour'">
          <i class="mdi mdi-human-pregnant me-1"></i>Active Labour
        </button>
        <button class="hms-tab" :class="{ active: tab==='partograph' }" @click="tab='partograph'" :disabled="!selectedLabour">
          <i class="mdi mdi-chart-line me-1"></i>Partograph
        </button>
        <button class="hms-tab" :class="{ active: tab==='anc' }" @click="tab='anc'">
          <i class="mdi mdi-mother-heart me-1"></i>ANC Visits
        </button>
        <button class="hms-tab" :class="{ active: tab==='delivery' }" @click="tab='delivery'">
          <i class="mdi mdi-baby-face-outline me-1"></i>Deliveries
        </button>
      </div>

      <!-- ============ ACTIVE LABOUR TAB ============ -->
      <div v-if="tab==='labour'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openLabourModal = true">
            <i class="mdi mdi-plus"></i> Open New Labour
          </button>
        </div>
        <div v-if="!labours.length" class="hms-empty">
          <i class="mdi mdi-bed-empty"></i>
          <h5>No active labours</h5>
        </div>
        <div v-else class="hms-labour-grid">
          <div v-for="l in labours" :key="l.labour_id" class="hms-labour-card" :class="labourClass(l)">
            <div class="hms-labour-head">
              <strong>{{ l.patient_name }}</strong>
              <span class="hms-muted-sm">{{ l.labour_number }}</span>
            </div>
            <div class="hms-labour-meta">
              <div><span class="hms-vk">G/P</span> {{ l.gravida || '?' }}/{{ l.para || '?' }}</div>
              <div><span class="hms-vk">Weeks</span> {{ l.gestation_weeks || '—' }}</div>
              <div><span class="hms-vk">In labour</span> {{ formatHours(l.minutes_in_labour) }}</div>
              <div v-if="l.max_dilation != null"><span class="hms-vk">Dilation</span> {{ l.max_dilation }}cm</div>
            </div>
            <div v-if="l.crossed_action == 1" class="hms-labour-alert hms-alert-action">
              <i class="mdi mdi-alert-octagon"></i> ACTION LINE — escalate now
            </div>
            <div v-else-if="l.crossed_alert == 1" class="hms-labour-alert hms-alert-warn">
              <i class="mdi mdi-alert"></i> Alert line crossed
            </div>
            <div class="hms-labour-actions">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openPartograph(l)">
                <i class="mdi mdi-chart-line"></i> Partograph
              </button>
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openDeliveryFor(l)">
                <i class="mdi mdi-baby-face"></i> Record Delivery
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ PARTOGRAPH TAB ============ -->
      <div v-if="tab==='partograph' && selectedLabour">
        <div class="hms-partogram-head">
          <div>
            <strong>{{ selectedLabour.patient_name }}</strong>
            <span class="hms-muted-sm ms-2">{{ selectedLabour.labour_number }} ·
              G/P {{ selectedLabour.gravida || '?' }}/{{ selectedLabour.para || '?' }} ·
              {{ selectedLabour.gestation_weeks || '?' }} wks
            </span>
          </div>
          <button class="hms-btn hms-btn-success hms-btn-sm" @click="entryModal = true">
            <i class="mdi mdi-plus"></i> Add Entry
          </button>
        </div>

        <!-- ===== CERVICAL DILATION CHART ===== -->
        <div class="hms-card mb-3">
          <div class="hms-card-head">Cervical dilation (cm) vs hours in active labour</div>
          <div class="hms-card-body">
            <svg :viewBox="'0 0 ' + (chartW + 60) + ' 280'" style="width:100%; max-width:900px; background:#fff;">
              <!-- Y axis grid 0-10cm -->
              <g v-for="cm in [0,2,4,6,8,10]" :key="'y'+cm">
                <line :x1="40" :y1="yCm(cm)" :x2="chartW + 40" :y2="yCm(cm)" stroke="#edf2f7" stroke-width="1" />
                <text :x="32" :y="yCm(cm) + 4" font-size="10" text-anchor="end" fill="#718096">{{ cm }}</text>
              </g>
              <!-- X axis grid every 2hr -->
              <g v-for="h in xHours" :key="'x'+h">
                <line :x1="xH(h) + 40" :y1="20" :x2="xH(h) + 40" :y2="240" stroke="#edf2f7" stroke-width="1" />
                <text :x="xH(h) + 40" :y="255" font-size="10" text-anchor="middle" fill="#718096">{{ h }}h</text>
              </g>
              <!-- ALERT LINE (1 cm/hr from 4cm) -->
              <line :x1="xH(0) + 40" :y1="yCm(4)" :x2="xH(6) + 40" :y2="yCm(10)" stroke="#dd6b20" stroke-width="2" stroke-dasharray="6,3" />
              <text :x="xH(3) + 50" :y="yCm(7) - 6" font-size="10" fill="#dd6b20" font-weight="600">ALERT</text>
              <!-- ACTION LINE (4 hr right of alert) -->
              <line :x1="xH(4) + 40" :y1="yCm(4)" :x2="xH(10) + 40" :y2="yCm(10)" stroke="#e53e3e" stroke-width="2" />
              <text :x="xH(7) + 50" :y="yCm(7) - 6" font-size="10" fill="#e53e3e" font-weight="600">ACTION</text>

              <!-- Plot points + line -->
              <polyline v-if="dilationPoints.length > 1" :points="dilationPoints.map(p => (xH(p.h) + 40) + ',' + yCm(p.d)).join(' ')" fill="none" stroke="#2b6cb0" stroke-width="2.5" />
              <g v-for="(p, i) in dilationPoints" :key="'pt'+i">
                <circle :cx="xH(p.h) + 40" :cy="yCm(p.d)" r="5" fill="#2b6cb0" stroke="#fff" stroke-width="2" />
                <text :x="xH(p.h) + 40" :y="yCm(p.d) - 8" font-size="9" text-anchor="middle" fill="#2c5282">{{ p.d }}</text>
              </g>

              <!-- Y axis label -->
              <text x="14" y="135" font-size="11" fill="#4a5568" transform="rotate(-90 14 135)">Dilation (cm)</text>
            </svg>
          </div>
        </div>

        <!-- ===== FETAL HEART RATE CHART ===== -->
        <div class="hms-card mb-3">
          <div class="hms-card-head">Fetal heart rate (normal 110-160)</div>
          <div class="hms-card-body">
            <svg :viewBox="'0 0 ' + (chartW + 60) + ' 200'" style="width:100%; max-width:900px; background:#fff;">
              <!-- Normal range band -->
              <rect :x="40" :y="yFhr(160)" :width="chartW" :height="yFhr(110) - yFhr(160)" fill="#f0fff4" />
              <!-- Y grid -->
              <g v-for="hr in [80,100,120,140,160,180]" :key="'fh'+hr">
                <line :x1="40" :y1="yFhr(hr)" :x2="chartW + 40" :y2="yFhr(hr)" stroke="#edf2f7" stroke-width="1" />
                <text :x="32" :y="yFhr(hr) + 4" font-size="10" text-anchor="end" fill="#718096">{{ hr }}</text>
              </g>
              <!-- X grid -->
              <g v-for="h in xHours" :key="'fx'+h">
                <line :x1="xH(h) + 40" :y1="20" :x2="xH(h) + 40" :y2="170" stroke="#edf2f7" stroke-width="1" />
                <text :x="xH(h) + 40" :y="185" font-size="10" text-anchor="middle" fill="#718096">{{ h }}h</text>
              </g>
              <!-- Plot -->
              <polyline v-if="fhrPoints.length > 1" :points="fhrPoints.map(p => (xH(p.h) + 40) + ',' + yFhr(p.v)).join(' ')" fill="none" stroke="#9b2c2c" stroke-width="2" />
              <g v-for="(p, i) in fhrPoints" :key="'fp'+i">
                <circle :cx="xH(p.h) + 40" :cy="yFhr(p.v)" r="4" :fill="p.v < 110 || p.v > 160 ? '#e53e3e' : '#9b2c2c'" stroke="#fff" stroke-width="1.5" />
              </g>
            </svg>
          </div>
        </div>

        <!-- ===== ENTRIES TABLE ===== -->
        <div class="hms-card">
          <div class="hms-card-head">All entries</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead>
                <tr>
                  <th>Recorded</th>
                  <th>Hr</th>
                  <th>FHR</th>
                  <th>Liquor</th>
                  <th>Mould</th>
                  <th>Dilation</th>
                  <th>Descent</th>
                  <th>Contractions</th>
                  <th>BP</th>
                  <th>Pulse</th>
                  <th>T°</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="e in entries" :key="e.entry_id" :class="entryClass(e)">
                  <td>{{ formatDateTime(e.recorded_at) }}</td>
                  <td>{{ e.hours_since_active }}</td>
                  <td :class="fhrClass(e.fetal_heart_rate)">{{ e.fetal_heart_rate || '—' }}</td>
                  <td>{{ e.liquor || '—' }}</td>
                  <td>{{ e.moulding || '0' }}</td>
                  <td><strong>{{ e.cervical_dilation_cm != null ? e.cervical_dilation_cm + 'cm' : '—' }}</strong></td>
                  <td>{{ e.descent_5ths != null ? e.descent_5ths + '/5' : '—' }}</td>
                  <td>{{ e.contractions_per_10min ? (e.contractions_per_10min + '/10 ' + (e.contraction_duration || '')) : '—' }}</td>
                  <td>{{ e.bp_systolic || '—' }}/{{ e.bp_diastolic || '—' }}</td>
                  <td>{{ e.pulse || '—' }}</td>
                  <td>{{ e.temperature || '—' }}</td>
                  <td>{{ e.notes || '' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ ANC TAB ============ -->
      <div v-if="tab==='anc'">
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2">
            <input v-model="ancSearch" class="hms-input" placeholder="Patient name / no" @keyup.enter="findAncPatient" />
            <button class="hms-btn hms-btn-blue" @click="findAncPatient">Search</button>
          </div>
          <button class="hms-btn hms-btn-success" @click="openAncEntry()">
            <i class="mdi mdi-plus"></i> New Visit
          </button>
        </div>
        <table v-if="ancVisits.length" class="hms-table">
          <thead>
            <tr><th>Date</th><th>Patient</th><th>Visit #</th><th>GA</th><th>BP</th><th>Wt</th><th>FH</th><th>FHR</th><th>Hb</th><th>Risk</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="v in ancVisits" :key="v.anc_visit_id" :class="ancRowClass(v)">
              <td>{{ v.visit_date }}</td>
              <td>{{ v.patient_name }} <span class="hms-muted-sm">{{ v.patient_no }}</span></td>
              <td>{{ v.visit_number }}</td>
              <td>{{ v.gestational_weeks || '—' }}<span v-if="v.gestational_days">+{{ v.gestational_days }}</span></td>
              <td>{{ v.bp_systolic || '—' }}/{{ v.bp_diastolic || '—' }}</td>
              <td>{{ v.weight_kg || '—' }}</td>
              <td>{{ v.fundal_height_cm || '—' }}</td>
              <td>{{ v.fetal_heart_rate || '—' }}</td>
              <td>{{ v.hb_level || '—' }}</td>
              <td>
                <span v-if="v.risk_category" class="hms-pill" :class="ancRiskPill(v.risk_category)">
                  {{ v.risk_category }} <span style="opacity:.7; margin-left:4px">{{ v.risk_score }}</span>
                </span>
                <span v-else class="hms-muted-sm">{{ v.complications || '—' }}</span>
                <div v-if="v.recommended_facility" class="hms-muted-sm">→ {{ v.recommended_facility }}</div>
              </td>
              <td style="white-space:nowrap">
                <button class="hms-btn-link" @click="openAncEntry(v)" title="Edit visit"><i class="mdi mdi-pencil"></i></button>
                <a class="hms-btn-link" :href="'#/maternal-safety?tab=risk&patient_id=' + v.patient_id" title="View in Maternal Safety"><i class="mdi mdi-shield-cross"></i></a>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="hms-empty"><i class="mdi mdi-mother-heart"></i><h5>No ANC visits on record</h5></div>
      </div>

      <!-- ============ DELIVERIES TAB ============ -->
      <div v-if="tab==='delivery'">
        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num">{{ delStats.total || 0 }}</div><div class="hms-stat-lbl">Total</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ delStats.svd || 0 }}</div><div class="hms-stat-lbl">SVD</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ delStats.cs || 0 }}</div><div class="hms-stat-lbl">CS</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ delStats.live_births || 0 }}</div><div class="hms-stat-lbl">Live births</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ delStats.stillbirths || 0 }}</div><div class="hms-stat-lbl">Stillbirths</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ delStats.pph_count || 0 }}</div><div class="hms-stat-lbl">PPH</div></div>
        </div>
        <div class="d-flex justify-content-end mb-2 gap-2">
          <button class="hms-btn hms-btn-success hms-btn-sm" @click="openDirectDelivery" title="Key in a delivery directly (e.g. born before arrival)"><i class="mdi mdi-plus"></i> Record Delivery</button>
          <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="backfillNewborns" title="Auto-register any unregistered babies"><i class="mdi mdi-baby-face"></i> Backfill newborns</button>
        </div>
        <table v-if="deliveries.length" class="hms-table">
          <thead><tr><th>When</th><th>Mother</th><th>Mode</th><th>Outcome</th><th>Sex</th><th>Wt(g)</th><th>APGAR 1/5</th><th>By</th><th>PPH</th><th>Baby</th><th>PNC</th></tr></thead>
          <tbody>
            <tr v-for="d in deliveries" :key="d.delivery_id">
              <td>{{ formatDateTime(d.delivered_at) }}</td>
              <td>{{ d.mother_name }} <span class="hms-muted-sm">{{ d.patient_no }}</span></td>
              <td>{{ d.mode }}</td>
              <td>{{ (d.baby_outcome || '').replace('_', ' ') }}</td>
              <td>{{ d.baby_sex || '—' }}</td>
              <td :class="d.baby_weight_g < 2500 ? 'st-bad' : ''">{{ d.baby_weight_g || '—' }}</td>
              <td>{{ d.apgar_1 || '—' }}/{{ d.apgar_5 || '—' }}</td>
              <td>{{ d.delivered_by_name || '—' }}</td>
              <td><span v-if="d.pph_occurred == 1" class="hms-pill hms-pill-danger">PPH</span></td>
              <td>
                <a v-if="d.baby_patient_id" class="hms-btn hms-btn-success hms-btn-sm" :href="'#/newborn/' + d.baby_patient_id" title="Open baby's life record"><i class="mdi mdi-baby-face"></i> Open</a>
                <button v-else-if="d.baby_outcome === 'live_birth'" class="hms-btn hms-btn-blue hms-btn-sm" @click="registerBaby(d)" title="Register newborn as patient"><i class="mdi mdi-account-plus"></i> Register</button>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <a class="hms-btn hms-btn-secondary hms-btn-sm" :href="'#/maternal-safety?tab=pnc&delivery_id=' + d.delivery_id" title="View PNC schedule"><i class="mdi mdi-calendar-check"></i> PNC</a>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="hms-empty"><i class="mdi mdi-baby-face-outline"></i><h5>No deliveries this period</h5></div>
      </div>
    </div>

    <!-- ===== OPEN LABOUR MODAL ===== -->
    <div v-if="openLabourModal" class="hms-modal-bg" @click.self="openLabourModal = false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head">
          <h5>Open Labour Record</h5>
          <button class="hms-icon-btn" @click="openLabourModal = false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="hms-label">Patient *</label>
              <div class="d-flex gap-2">
                <input v-model="newLabour.search" class="hms-input" placeholder="Name or patient no" @keyup.enter="findNewLabourPatient" />
                <button class="hms-btn hms-btn-blue" @click="findNewLabourPatient">Find</button>
              </div>
              <div v-if="newLabour.patient" class="hms-patient-chip">
                <i class="mdi mdi-account"></i> {{ newLabour.patient.first_name }} {{ newLabour.patient.last_name }} ({{ newLabour.patient.patient_no }})
              </div>
            </div>
            <div class="col-3"><label class="hms-label">Gravida</label><input v-model.number="newLabour.gravida" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Para</label><input v-model.number="newLabour.para" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Weeks</label><input v-model.number="newLabour.gestation_weeks" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">EDD</label><input v-model="newLabour.edd" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Labour onset</label><input v-model="newLabour.labour_onset_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Membranes ruptured</label><input v-model="newLabour.membranes_ruptured_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Liquor</label>
              <select v-model="newLabour.liquor_color" class="hms-input">
                <option value="intact">Intact</option><option value="clear">Clear</option>
                <option value="meconium">Meconium</option><option value="blood">Blood</option><option value="absent">Absent</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Presentation</label>
              <select v-model="newLabour.presentation" class="hms-input">
                <option value="cephalic">Cephalic</option><option value="breech">Breech</option>
                <option value="transverse">Transverse</option><option value="oblique">Oblique</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Risk factors</label><textarea v-model="newLabour.risk_factors" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="openLabourModal = false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !newLabour.patient" @click="saveOpenLabour">{{ saving ? 'Saving…' : 'Open Labour' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== PARTOGRAPH ENTRY MODAL ===== -->
    <div v-if="entryModal" class="hms-modal-bg" @click.self="entryModal = false">
      <div class="hms-modal" style="width:720px">
        <div class="hms-modal-head">
          <h5>Partograph entry</h5>
          <button class="hms-icon-btn" @click="entryModal = false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">FHR</label><input v-model.number="entry.fetal_heart_rate" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Liquor</label>
              <select v-model="entry.liquor" class="hms-input">
                <option value="">—</option><option value="I">I — Intact</option><option value="C">C — Clear</option>
                <option value="M">M — Meconium</option><option value="B">B — Blood</option><option value="A">A — Absent</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Moulding</label>
              <select v-model="entry.moulding" class="hms-input">
                <option value="0">0</option><option value="+">+</option><option value="++">++</option><option value="+++">+++</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Descent /5</label><input v-model.number="entry.descent_5ths" type="number" min="0" max="5" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Dilation (cm)</label><input v-model.number="entry.cervical_dilation_cm" type="number" min="0" max="10" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Contractions /10min</label><input v-model.number="entry.contractions_per_10min" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Duration</label>
              <select v-model="entry.contraction_duration" class="hms-input">
                <option value="">—</option><option value="<20">&lt;20s</option><option value="20-40">20-40s</option><option value=">40">&gt;40s</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Oxytocin (drops/min)</label><input v-model.number="entry.oxytocin_drops_per_min" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BP Sys</label><input v-model.number="entry.bp_systolic" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BP Dia</label><input v-model.number="entry.bp_diastolic" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Pulse</label><input v-model.number="entry.pulse" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Temp °C</label><input v-model.number="entry.temperature" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="entry.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="entryModal = false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveEntry">{{ saving ? 'Saving…' : 'Save Entry' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== ANC ENTRY MODAL ===== -->
    <div v-if="ancModal" class="hms-modal-bg" @click.self="ancModal = false">
      <div class="hms-modal" style="width:760px">
        <div class="hms-modal-head">
          <h5>{{ anc.anc_visit_id ? 'Edit ANC Visit' : 'New ANC Visit' }}</h5>
          <button class="hms-icon-btn" @click="ancModal = false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12" v-if="!anc.anc_visit_id">
              <label class="hms-label">Patient *</label>
              <div class="d-flex gap-2">
                <input v-model="anc.search" class="hms-input" placeholder="Name or patient no" @keyup.enter="findAncEntryPatient" />
                <button class="hms-btn hms-btn-blue" @click="findAncEntryPatient">Find</button>
              </div>
              <div v-if="anc.patient_name" class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ anc.patient_name }}</div>
            </div>
            <div class="col-3"><label class="hms-label">Visit #</label><input v-model.number="anc.visit_number" type="number" min="1" max="8" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Visit date</label><input v-model="anc.visit_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">LMP</label><input v-model="anc.lmp_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">EDD</label><input v-model="anc.edd" type="date" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Wks</label><input v-model.number="anc.gestational_weeks" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Gravida</label><input v-model.number="anc.gravida" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Para</label><input v-model.number="anc.para" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">BP Sys</label><input v-model.number="anc.bp_systolic" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">BP Dia</label><input v-model.number="anc.bp_diastolic" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Wt kg</label><input v-model.number="anc.weight_kg" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Fundal ht (cm)</label><input v-model.number="anc.fundal_height_cm" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">FHR</label><input v-model.number="anc.fetal_heart_rate" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Hb (g/dL)</label><input v-model.number="anc.hb_level" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Urine protein</label>
              <select v-model="anc.urine_protein" class="hms-input">
                <option value="nil">nil</option><option value="trace">trace</option>
                <option value="+">+</option><option value="++">++</option><option value="+++">+++</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">HIV</label>
              <select v-model="anc.hiv_status" class="hms-input">
                <option value="unknown">Unknown</option><option value="negative">Negative</option>
                <option value="positive">Positive</option><option value="declined">Declined</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">VDRL</label>
              <select v-model="anc.vdrl_status" class="hms-input">
                <option value="unknown">Unknown</option><option value="negative">Negative</option>
                <option value="positive">Positive</option><option value="declined">Declined</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">IPT-p dose</label><input v-model.number="anc.iptp_dose" type="number" min="0" max="5" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">TT dose</label><input v-model.number="anc.tt_dose" type="number" min="0" max="5" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end gap-3">
              <label><input type="checkbox" v-model="anc.iron_folate_given" :true-value="1" :false-value="0" /> Iron/Folate</label>
              <label><input type="checkbox" v-model="anc.llin_given"        :true-value="1" :false-value="0" /> LLIN</label>
            </div>
            <!-- ANTENATAL EPDS + IPV (added Session: Women's lifecycle) -->
            <div class="col-12 mt-2">
              <div class="hms-anc-section">
                <i class="mdi mdi-brain"></i> <strong>Mental health (antenatal EPDS)</strong> — recommended at booking, mid-pregnancy, third trimester
              </div>
            </div>
            <div class="col-3"><label class="hms-label">EPDS score (0-30)</label><input v-model.number="anc.epds_score" type="number" min="0" max="30" class="hms-input" /></div>
            <div class="col-4 d-flex align-items-end">
              <label class="me-3"><input type="checkbox" v-model="anc.epds_q10_self_harm" :true-value="1" :false-value="0" /> <strong>Q10 self-harm positive</strong></label>
            </div>
            <div class="col-5 d-flex align-items-end">
              <label><input type="checkbox" v-model="anc.antenatal_depression_referred" :true-value="1" :false-value="0" /> Referred to mental health</label>
            </div>
            <div class="col-12">
              <div class="hms-muted-sm">0-9 low · 10-12 possible depression · ≥13 likely depression. <strong>Any Q10 positive → same-day referral.</strong></div>
            </div>

            <div class="col-12 mt-2">
              <div class="hms-anc-section">
                <i class="mdi mdi-shield-account"></i> <strong>IPV screening</strong> — recommended at every ANC, in private
              </div>
            </div>
            <div class="col-6 d-flex align-items-end">
              <label><input type="checkbox" v-model="anc.ipv_screened" :true-value="1" :false-value="0" /> IPV screened today (record details in Women's Health module)</label>
            </div>
            <div class="col-6 d-flex align-items-end">
              <a class="hms-btn hms-btn-secondary hms-btn-sm" :href="'#/womens-health'" target="_blank"><i class="mdi mdi-open-in-new"></i> Open IPV screening form</a>
            </div>

            <div class="col-12 mt-2"><label class="hms-label">Risk factors / complications</label><textarea v-model="anc.complications" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Next visit date</label><input v-model="anc.next_visit_date" type="date" class="hms-input" /></div>
            <div class="col-6 d-flex align-items-end">
              <label><input type="checkbox" v-model="anc.referral_made" :true-value="1" :false-value="0" /> Referral made</label>
            </div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="ancModal = false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveAnc">{{ saving ? 'Saving…' : 'Save Visit' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== DELIVERY MODAL ===== -->
    <div v-if="delModal" class="hms-modal-bg" @click.self="delModal = false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head">
          <h5>Record Delivery<span v-if="delivery._mother"> — {{ delivery._mother }}</span></h5>
          <button class="hms-icon-btn" @click="delModal = false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <!-- Patient picker — only when recording a delivery directly (no prior labour) -->
          <div v-if="!delivery.patient_id" class="mb-3">
            <div class="hms-section-label">MOTHER (PATIENT) *</div>
            <div class="d-flex gap-2">
              <input v-model="delSearch" class="hms-input" placeholder="Search name or patient no" @keyup.enter="findDeliveryPatient" />
              <button class="hms-btn hms-btn-blue" @click="findDeliveryPatient">Find</button>
            </div>
            <div v-for="p in delSearchResults" :key="p.patient_id" class="hms-patient-chip" style="cursor:pointer" @click="pickDeliveryPatient(p)">
              <i class="mdi mdi-account"></i> {{ p.first_name }} {{ p.last_name }} ({{ p.patient_no }})
            </div>
            <div v-if="delSearched && !delSearchResults.length" class="hms-muted-sm mt-1">No patient found — register the mother first.</div>
          </div>

          <div class="hms-section-label">DELIVERY</div>
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Delivered at *</label><input v-model="delivery.delivered_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Mode *</label>
              <select v-model="delivery.mode" class="hms-input">
                <option value="SVD">SVD</option><option value="CS_emergency">CS — Emergency</option>
                <option value="CS_elective">CS — Elective</option><option value="vacuum">Vacuum</option>
                <option value="forceps">Forceps</option><option value="breech">Breech</option>
                <option value="assisted_breech">Assisted breech</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Blood loss (ml)</label><input v-model.number="delivery.blood_loss_ml" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Perineal tear</label>
              <select v-model.number="delivery.perineal_tear_degree" class="hms-input">
                <option :value="0">None</option><option :value="1">1°</option><option :value="2">2°</option><option :value="3">3°</option><option :value="4">4°</option>
              </select>
            </div>
            <div class="col-3 d-flex align-items-end gap-3">
              <label><input type="checkbox" v-model="delivery.episiotomy" :true-value="1" :false-value="0" /> Episiotomy</label>
            </div>
            <div class="col-3 d-flex align-items-end gap-3">
              <label><input type="checkbox" v-model="delivery.pph_occurred" :true-value="1" :false-value="0" /> PPH</label>
              <label><input type="checkbox" v-model="delivery.oxytocin_amtsl" :true-value="1" :false-value="0" /> AMTSL</label>
            </div>
            <div class="col-3 d-flex align-items-end gap-3">
              <label><input type="checkbox" v-model="delivery.cord_around_neck" :true-value="1" :false-value="0" /> Cord round neck</label>
            </div>
          </div>

          <div class="hms-section-label mt-3">BABY</div>
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Outcome</label>
              <select v-model="delivery.baby_outcome" class="hms-input">
                <option value="live_birth">Live birth</option>
                <option value="fresh_stillbirth">Fresh stillbirth</option>
                <option value="macerated_stillbirth">Macerated stillbirth</option>
                <option value="neonatal_death">Neonatal death</option>
              </select>
            </div>
            <div class="col-2"><label class="hms-label">Sex</label>
              <select v-model="delivery.baby_sex" class="hms-input">
                <option value="">—</option><option value="M">M</option><option value="F">F</option><option value="ambiguous">Amb</option>
              </select>
            </div>
            <div class="col-2"><label class="hms-label">Wt (g)</label><input v-model.number="delivery.baby_weight_g" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Length cm</label><input v-model.number="delivery.baby_length_cm" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Head circ cm</label><input v-model.number="delivery.head_circumference_cm" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">APGAR 1'</label><input v-model.number="delivery.apgar_1" type="number" min="0" max="10" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">APGAR 5'</label><input v-model.number="delivery.apgar_5" type="number" min="0" max="10" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">APGAR 10'</label><input v-model.number="delivery.apgar_10" type="number" min="0" max="10" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Resuscitation</label>
              <select v-model="delivery.resuscitation" class="hms-input">
                <option value="none">None</option><option value="stimulation">Stimulation</option>
                <option value="bag_mask">Bag-mask</option><option value="cpap">CPAP</option><option value="intubation">Intubation</option>
              </select>
            </div>
            <div class="col-3 d-flex align-items-end gap-2 flex-wrap">
              <label><input type="checkbox" v-model="delivery.bf_within_1hr" :true-value="1" :false-value="0" /> BF&lt;1hr</label>
              <label><input type="checkbox" v-model="delivery.vit_k_given" :true-value="1" :false-value="0" /> Vit K</label>
              <label><input type="checkbox" v-model="delivery.bcg_given" :true-value="1" :false-value="0" /> BCG</label>
              <label><input type="checkbox" v-model="delivery.opv_given" :true-value="1" :false-value="0" /> OPV</label>
            </div>
            <div class="col-3"><label><input type="checkbox" v-model="delivery.nicu_admission" :true-value="1" :false-value="0" /> NICU admission</label></div>
            <div class="col-12" v-if="delivery.nicu_admission == 1"><label class="hms-label">NICU reason</label><input v-model="delivery.nicu_reason" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Complications / notes</label><textarea v-model="delivery.complications" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="delModal = false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !delivery.patient_id || !delivery.delivered_at" @click="saveDelivery">{{ saving ? 'Saving…' : 'Save Delivery' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MaternityManagement",
  components: { Navigation },
  data() {
    return {
      tab: 'labour',
      saving: false,
      labours: [], labourStats: {},
      ancVisits: [], ancStats: {},
      deliveries: [], delStats: {},

      selectedLabour: null,
      entries: [],

      openLabourModal: false,
      newLabour: this.blankLabour(),

      entryModal: false,
      entry: this.blankEntry(),

      ancSearch: '',
      ancModal: false,
      anc: this.blankAnc(),

      delModal: false,
      delSearch: '', delSearchResults: [], delSearched: false,
      delivery: this.blankDelivery(),

      chartW: 600,
      xHours: [0,2,4,6,8,10,12]
    };
  },
  computed: {
    dilationPoints() {
      return this.entries
        .filter(e => e.cervical_dilation_cm != null && e.hours_since_active != null)
        .map(e => ({ h: parseFloat(e.hours_since_active), d: parseInt(e.cervical_dilation_cm) }));
    },
    fhrPoints() {
      return this.entries
        .filter(e => e.fetal_heart_rate != null && e.hours_since_active != null)
        .map(e => ({ h: parseFloat(e.hours_since_active), v: parseInt(e.fetal_heart_rate) }));
    }
  },
  mounted() {
    this.loadAll();
    // Deep-link support: /maternity?tab=anc&visit_id=42 or ?tab=delivery
    const q = this.$route.query || {};
    if (q.tab && ['labour','partograph','anc','delivery'].includes(q.tab)) this.tab = q.tab;
    if (q.visit_id) {
      this.tab = 'anc';
      this.$nextTick(async () => {
        await this.loadAnc();
        const v = (this.ancVisits || []).find(x => String(x.anc_visit_id) === String(q.visit_id));
        if (v) this.openAncEntry(v);
      });
    }
  },
  methods: {
    blankLabour() { return { search:'', patient:null, gravida:null, para:null, gestation_weeks:null, edd:'', labour_onset_at:'', membranes_ruptured_at:'', liquor_color:'intact', presentation:'cephalic', risk_factors:'' }; },
    blankEntry() { return { fetal_heart_rate:null, liquor:'', moulding:'0', cervical_dilation_cm:null, descent_5ths:null, contractions_per_10min:null, contraction_duration:'', oxytocin_drops_per_min:null, bp_systolic:null, bp_diastolic:null, pulse:null, temperature:null, notes:'' }; },
    blankAnc()   { return { search:'', patient_id:null, patient_name:'', visit_number:1, visit_date:new Date().toISOString().substring(0,10), lmp_date:'', edd:'', gestational_weeks:null, gravida:null, para:null, bp_systolic:null, bp_diastolic:null, weight_kg:null, fundal_height_cm:null, fetal_heart_rate:null, hb_level:null, urine_protein:'nil', hiv_status:'unknown', vdrl_status:'unknown', iptp_dose:0, tt_dose:0, iron_folate_given:0, llin_given:0, referral_made:0, complications:'', next_visit_date:'', epds_score:null, epds_q10_self_harm:0, antenatal_depression_referred:0, ipv_screened:0 }; },
    blankDelivery() { return { _mother:'', patient_id:null, labour_id:null, delivered_at:'', mode:'SVD', presenting_part:'vertex', placenta_complete:1, blood_loss_ml:null, pph_occurred:0, perineal_tear_degree:0, episiotomy:0, oxytocin_amtsl:1, cord_around_neck:0, baby_outcome:'live_birth', baby_sex:'', baby_weight_g:null, baby_length_cm:null, head_circumference_cm:null, apgar_1:null, apgar_5:null, apgar_10:null, cried_immediately:1, resuscitation:'none', bf_within_1hr:0, vit_k_given:0, tetracycline_eye:0, bcg_given:0, opv_given:0, nicu_admission:0, nicu_reason:'', complications:'' }; },

    async loadAll() {
      await Promise.all([this.loadLabours(), this.loadAnc(), this.loadDeliveries()]);
    },
    async loadLabours() {
      try {
        const r = await executeJson({ function: 'getActiveLabour' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.labours = d.Labours || [];
          this.labourStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadAnc() {
      try {
        const r = await executeJson({ function: 'getAncVisits' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.ancVisits = d.Visits || [];
          this.ancStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadDeliveries() {
      try {
        const r = await executeJson({ function: 'getDeliveries', period: 'this_month' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.deliveries = d.Deliveries || [];
          this.delStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    async findNewLabourPatient() {
      const q = (this.newLabour.search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const p = (d.Patients || d.patients || [])[0];
        if (p) this.newLabour.patient = p;
        else alert('No patient found.');
      }
    },
    async saveOpenLabour() {
      if (!this.newLabour.patient) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'openLabour', patient_id: this.newLabour.patient.patient_id, ...this.newLabour, search: undefined, patient: undefined }, hms);
        if (r.data && r.data.success) {
          this.openLabourModal = false;
          this.newLabour = this.blankLabour();
          await this.loadLabours();
        } else { alert((r.data && r.data.message) || 'Failed.'); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openPartograph(l) {
      this.selectedLabour = l;
      this.tab = 'partograph';
      try {
        const r = await executeJson({ function: 'getPartograph', labour_id: l.labour_id }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.entries = d.Entries || [];
        }
      } catch (e) { console.error(e); }
    },
    async saveEntry() {
      if (!this.selectedLabour) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'addPartographEntry', labour_id: this.selectedLabour.labour_id, ...this.entry }, hms);
        if (r.data && r.data.success) {
          if (r.data.message) alert(r.data.message);
          this.entry = this.blankEntry();
          this.entryModal = false;
          await this.openPartograph(this.selectedLabour);
          await this.loadLabours();
        }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openDeliveryFor(l) {
      this.delivery = this.blankDelivery();
      this.delivery.patient_id = l.patient_id;
      this.delivery.labour_id  = l.labour_id;
      this.delivery._mother    = l.patient_name;
      this.delivery.delivered_at = new Date().toISOString().substring(0,16);
      this.delModal = true;
    },
    openDirectDelivery() {
      // Key in a delivery directly (no prior labour record) — e.g. born before arrival.
      this.delivery = this.blankDelivery();
      this.delivery.delivered_at = new Date().toISOString().substring(0,16);
      this.delSearch = ''; this.delSearchResults = []; this.delSearched = false;
      this.delModal = true;
    },
    async findDeliveryPatient() {
      const q = (this.delSearch || '').trim();
      if (!q) return;
      this.delSearched = true;
      try {
        const r = await executeJson({ function: 'searchPatients', q: q }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.delSearchResults = (d.Patients || d.patients || []).slice(0, 6);
        } else { this.delSearchResults = []; }
      } catch (e) { this.delSearchResults = []; }
    },
    pickDeliveryPatient(p) {
      this.delivery.patient_id = p.patient_id;
      this.delivery._mother = (p.first_name || '') + ' ' + (p.last_name || '') + ' (' + (p.patient_no || '') + ')';
      this.delSearchResults = [];
    },
    async saveDelivery() {
      this.saving = true;
      try {
        const params = { function: 'recordDelivery', ...this.delivery };
        delete params._mother;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) {
          this.delModal = false;
          this.delivery = this.blankDelivery();
          await Promise.all([this.loadLabours(), this.loadDeliveries()]);
          this.tab = 'delivery';
        } else { alert((r.data && r.data.message) || 'Failed.'); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async findAncPatient() {
      const q = (this.ancSearch || '').trim();
      if (!q) { await this.loadAnc(); return; }
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const p = (d.Patients || d.patients || [])[0];
        if (p) {
          const v = await executeJson({ function: 'getAncVisits', patient_id: p.patient_id }, hms);
          if (v.data && v.data.success) {
            const dd = v.data.data || {};
            this.ancVisits = dd.Visits || [];
          }
        }
      }
    },
    openAncEntry(v) {
      if (v) {
        this.anc = { ...this.blankAnc(), ...v, search:'', patient_name: v.patient_name };
      } else {
        this.anc = this.blankAnc();
      }
      this.ancModal = true;
    },
    async findAncEntryPatient() {
      const q = (this.anc.search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const p = (d.Patients || d.patients || [])[0];
        if (p) { this.anc.patient_id = p.patient_id; this.anc.patient_name = p.first_name + ' ' + p.last_name; }
        else alert('No patient found.');
      }
    },
    async saveAnc() {
      if (!this.anc.patient_id && !this.anc.anc_visit_id) { alert('Select a patient.'); return; }
      this.saving = true;
      try {
        const params = { function: 'saveAncVisit', ...this.anc };
        delete params.search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) {
          // Surface antenatal mental-health alerts to the clinician immediately
          if (r.data.data && r.data.data.alerts && r.data.data.alerts.length) {
            const types = r.data.data.alerts.map(a => a.type);
            if (types.indexOf('epds_q10') !== -1) {
              alert('🚨 EPDS Q10 self-harm positive — SAME-DAY psychiatry referral. Critical alert opened.');
            } else if (types.indexOf('epds_likely') !== -1) {
              alert('EPDS ≥13: likely antenatal depression — mental health referral indicated.');
            } else if (types.indexOf('epds_possible') !== -1) {
              alert('EPDS 10-12: possible depression — repeat in 2 weeks + offer support.');
            }
          }
          this.ancModal = false;
          await this.loadAnc();
        } else { alert((r.data && r.data.message) || 'Failed.'); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    ancRowClass(v) {
      if (v.risk_category === 'critical') return 'row-action';
      if (v.risk_category === 'high') return 'row-warn';
      if (v.bp_systolic >= 140 || v.bp_diastolic >= 90) return 'row-warn';
      if (v.hb_level && v.hb_level < 11) return 'row-warn';
      return '';
    },
    ancRiskPill(c) { return ({ critical:'hms-pill-danger', high:'hms-pill-warn', moderate:'hms-pill-info', low:'hms-pill-success' })[c] || 'hms-pill-muted'; },

    async registerBaby(d) {
      const firstName = prompt('Baby first name (leave blank for "Baby of ' + d.mother_name + '"):');
      if (firstName === null) return;
      try {
        const payload = { function: 'registerNewbornFromDelivery', delivery_id: d.delivery_id };
        if (firstName) payload.first_name = firstName;
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          await this.loadDeliveries();
          if (r.data.data && r.data.data.baby_patient_id) {
            if (confirm('Baby registered as patient ' + r.data.data.patient_no + '. Open the life record now?')) {
              this.$router.push('/newborn/' + r.data.data.baby_patient_id);
            }
          }
        } else if (r.data) { alert(r.data.message || 'Failed.'); }
      } catch (e) { console.error(e); }
    },
    async backfillNewborns() {
      if (!confirm('Auto-register every unregistered live-birth baby as a patient with a Notification of Birth number?')) return;
      const r = await executeJson({ function: 'backfillNewborns' }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        alert((d.created || 0) + ' newborns registered. ' + (d.skipped || 0) + ' already had records.');
        await this.loadDeliveries();
      } else if (r.data) { alert(r.data.message || 'Failed.'); }
    },

    // ===== chart helpers =====
    yCm(cm)  { return 240 - (cm * 22); },          // 0cm at y=240, 10cm at y=20
    yFhr(hr) { return 170 - ((hr - 60) * 1.1); },  // 60-220 range
    xH(h)    { return Math.min(this.chartW, h * (this.chartW / 12)); },

    labourClass(l) {
      if (l.crossed_action == 1) return 'st-action';
      if (l.crossed_alert == 1)  return 'st-alert';
      return '';
    },
    entryClass(e) {
      if (e.crossed_action_line == 1) return 'row-action';
      if (e.crossed_alert_line  == 1) return 'row-warn';
      return '';
    },
    fhrClass(v) {
      if (v == null) return '';
      if (v < 110 || v > 160) return 'st-bad';
      return '';
    },

    formatHours(m) {
      if (m == null) return '—';
      const h = Math.floor(m / 60);
      return h + 'h ' + Math.round(m - h * 60) + 'm';
    },
    formatDateTime(s) {
      if (!s) return '—';
      const d = new Date(s.replace ? s.replace(' ', 'T') : s);
      return isNaN(d) ? s : d.toLocaleString();
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-danger  { color: #c53030; }
.hms-stat-warning { color: #c05621; }
.hms-stat-info    { color: #2c5282; }
.hms-stat-success { color: #276749; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-labour-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.hms-labour-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #4299e1; border-radius: 8px; padding: 14px; }
.hms-labour-card.st-alert  { border-left-color: #dd6b20; background: #fffaf0; }
.hms-labour-card.st-action { border-left-color: #e53e3e; background: #fff5f5; }
.hms-labour-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
.hms-labour-meta { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; font-size: 13px; margin-bottom: 8px; }
.hms-vk { display: inline-block; min-width: 60px; color: #a0aec0; font-size: 10px; text-transform: uppercase; }
.hms-labour-alert { padding: 6px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; margin-bottom: 8px; }
.hms-alert-warn   { background: #feebc8; color: #7b341e; }
.hms-alert-action { background: #fed7d7; color: #742a2a; }
.hms-labour-actions { display: flex; gap: 6px; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-partogram-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.hms-table th { background: #f7fafc; padding: 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 6px 8px; border-bottom: 1px solid #edf2f7; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }
.st-bad { color: #c53030; font-weight: 600; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #a0aec0; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin-bottom: 8px; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.hms-anc-section { background: #f7fafc; border-left: 3px solid #2b6cb0; padding: 6px 10px; font-size: 12px; color: #2d3748; margin: 6px 0; border-radius: 3px; }
.hms-anc-section .mdi { color: #2b6cb0; margin-right: 4px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-muted-sm { color: #a0aec0; font-size: 11px; }

.row-warn   { background: #fffaf0; }
.row-action { background: #fff5f5; }
</style>
